"use client"
import React from 'react'
import { createPortal } from 'react-dom'
import { Menu, Settings } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNavLinks } from '@/modules/navigation/hooks/useNavLinks'
import { cn } from '@/lib/utils'
import { UserDrawerMenu } from '@/modules/navigation/components/UserDrawerMenu'

export function MobileBottomNav() {
  const { links, effectiveRole } = useNavLinks()
  const loc = useLocation()
  const nav = useNavigate()
  const [openDrawer, setOpenDrawer] = React.useState(false)

  const isMaster = effectiveRole === 'master'
  // Suporta member, afiliado e master; renderiza apenas em telas pequenas (via Tailwind)
  if (!['member', 'afiliado', 'master'].includes(effectiveRole || '')) return null

  const oportunidades = links.find((l) => l.to === '/oportunidades')
  const analiseCripto = links.find((l) => l.to === '/analise-cripto')
  const visoes = links.find((l) => l.to === '/visoes')
  const chat = links.find((l) => l.to === '/chat')
  const settings = isMaster ? links.find((l) => l.to === '/settings') : null
  const oportunidadesTo = oportunidades?.to || '/oportunidades'
  const settingsTo = settings?.to || '/settings'

  const primaryLinks = [
    oportunidadesTo,
    analiseCripto?.to,
    visoes?.to,
    chat?.to,
    isMaster ? settingsTo : null,
  ].filter(Boolean) as string[]

  const showAnaliseCripto = Boolean(analiseCripto)
  const showVisoes = Boolean(visoes)
  const showChat = Boolean(chat)
  const showSettings = isMaster
  const totalCols = primaryLinks.length + 1

  const BUTTON_BASE_CLASS = 'flex flex-col items-center justify-center gap-0.5 py-1'
  const ICON_CLASS = 'h-5 w-5'
  const LABEL_CLASS = 'max-w-full truncate text-[10px] leading-none'

  function isActive(path: string) {
    return loc.pathname === path
  }

  function renderNavIcon(icon: React.ReactNode | null | undefined, active: boolean) {
    if (!icon) return null
    return React.cloneElement(icon as any, {
      className: cn(ICON_CLASS, active ? 'opacity-100' : 'opacity-80'),
    })
  }

  const settingsIcon = settings?.icon ?? <Settings className={ICON_CLASS} />

  if (typeof document === 'undefined') return null

  const node = (
    <>
      <nav className="lg:hidden fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div
          className={cn(
            'mx-auto grid items-stretch gap-1 px-3 py-1.5',
            totalCols >= 6
              ? 'max-w-[1040px] grid-cols-6'
              : totalCols === 5
                ? 'max-w-[900px] grid-cols-5'
                : totalCols === 4
                  ? 'max-w-[760px] grid-cols-4'
                  : totalCols === 3
                    ? 'max-w-[640px] grid-cols-3'
                    : 'max-w-[520px] grid-cols-2',
          )}
          role="tablist"
          aria-label="Navegacao principal"
        >
          <button
            type="button"
            onClick={() => nav(oportunidadesTo)}
            className={cn(
              BUTTON_BASE_CLASS,
              isActive(oportunidadesTo) ? 'text-foreground' : 'text-foreground/70',
            )}
          >
            {renderNavIcon(oportunidades?.icon, isActive(oportunidadesTo))}
            <span className={LABEL_CLASS}>Oportunidades</span>
          </button>

          {showAnaliseCripto ? (
            <button
              type="button"
              onClick={() => analiseCripto && nav(analiseCripto.to)}
              className={cn(
                BUTTON_BASE_CLASS,
                analiseCripto && isActive(analiseCripto.to) ? 'text-foreground' : 'text-foreground/70',
              )}
            >
              {renderNavIcon(analiseCripto?.icon, !!analiseCripto && isActive(analiseCripto.to))}
              <span className={LABEL_CLASS}>{analiseCripto?.label || 'Analise Cripto'}</span>
            </button>
          ) : null}

          {showVisoes ? (
            <button
              type="button"
              onClick={() => visoes && nav(visoes.to)}
              className={cn(
                BUTTON_BASE_CLASS,
                visoes && isActive(visoes.to) ? 'text-foreground' : 'text-foreground/70',
              )}
            >
              {renderNavIcon(visoes?.icon, !!visoes && isActive(visoes.to))}
              <span className={LABEL_CLASS}>{visoes?.label || 'Visoes'}</span>
            </button>
          ) : null}

          {showChat ? (
            <button
              type="button"
              onClick={() => chat && nav(chat.to)}
              className={cn(
                BUTTON_BASE_CLASS,
                chat && isActive(chat.to) ? 'text-foreground' : 'text-foreground/70',
              )}
            >
              {renderNavIcon(chat?.icon, !!chat && isActive(chat.to))}
              <span className={LABEL_CLASS}>{chat?.label || 'Chat'}</span>
            </button>
          ) : null}

          {showSettings ? (
            <button
              type="button"
              onClick={() => nav(settingsTo)}
              className={cn(
                BUTTON_BASE_CLASS,
                isActive(settingsTo) ? 'text-foreground' : 'text-foreground/70',
              )}
            >
              {renderNavIcon(settingsIcon, isActive(settingsTo))}
              <span className={LABEL_CLASS}>Configuracoes</span>
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => setOpenDrawer(true)}
            className={cn(BUTTON_BASE_CLASS, 'text-foreground/70')}
            aria-haspopup="dialog"
            aria-expanded={openDrawer}
            aria-controls="right-drawer"
          >
            <Menu className={ICON_CLASS} />
            <span className={LABEL_CLASS}>Menu</span>
          </button>
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" />
      </nav>

      <UserDrawerMenu
        open={openDrawer}
        onOpenChange={setOpenDrawer}
        items={[
          ...links
            .filter((l) =>
              ![oportunidadesTo, analiseCripto?.to, visoes?.to, chat?.to, showSettings ? settingsTo : null]
                .filter(Boolean)
                .includes(l.to),
            )
            .map((l) => ({ label: l.label, to: l.to })),
          { label: 'Gerenciar APP', to: '/gerenciar-app' },
          ...(isMaster ? [] : [{ label: 'Configuracoes', to: '/user-settings?sec=notifications' }]),
          { label: 'Editar perfil', to: '/profile' },
        ]}
      />
    </>
  )

  return createPortal(node, document.body)
}
